import type { QuickstartTheme } from '../../types/theme';
import type { StageWashStops } from '../DeviceFallback/mobileStageWash';

/**
 * Anika's UI Shell fade, from `anika-dropin-ui`
 * `DeviceFallback/mobileStageWash.ts`. Icon Drop on that branch called
 * `buildMediaMask()` / `buildContinuumWash()` with these exact stops on a
 * full-bleed stage (no cropped media band). Copied here so Icon Drop can
 * keep them if the email-fallback wash later diverges.
 */
export const ICON_DROP_WASH_PRESET_LOWER = {
  mediaBandPercent: 100,
  washStops: [
    [0, 0],
    [26, 0],
    [38, 0.16],
    [50, 0.36],
    [60, 0.56],
    [70, 0.75],
    [80, 0.9],
    [90, 1],
    [100, 1],
  ] as const satisfies StageWashStops,
  maskStops: [
    [0, 1],
    [28, 1],
    [40, 0.92],
    [52, 0.72],
    [64, 0.48],
    [74, 0.28],
    [84, 0.12],
    [92, 0.03],
    [100, 0],
  ] as const satisfies StageWashStops,
};

/** Alias so existing `raised` Redis values resolve to the same UI Shell wash. */
export const ICON_DROP_WASH_PRESET_RAISED = ICON_DROP_WASH_PRESET_LOWER;

export type IconDropWashPresetName = 'raised' | 'lower';

export interface IconDropStageWash {
  mediaBand: string;
  washStops: StageWashStops;
  maskStops: StageWashStops;
}

const PRESETS = {
  raised: ICON_DROP_WASH_PRESET_RAISED,
  lower: ICON_DROP_WASH_PRESET_LOWER,
} as const;

const lastClear = (stops: StageWashStops): number => {
  let clear = 0;
  for (const [percent, alpha] of stops) {
    if (alpha > 0) return clear;
    clear = percent;
  }
  return clear;
};

const firstSolid = (stops: StageWashStops): number =>
  stops.find(([, alpha]) => alpha >= 1)?.[0] ?? 100;

const remapPercent = (percent: number, fromClear: number, fromSolid: number, toClear: number, toSolid: number): number => {
  if (percent <= fromClear) {
    return fromClear === 0 ? 0 : (percent / fromClear) * toClear;
  }
  if (percent >= fromSolid) {
    const span = 100 - fromSolid;
    return span === 0 ? toSolid : toSolid + ((percent - fromSolid) / span) * (100 - toSolid);
  }
  const span = fromSolid - fromClear;
  return span === 0 ? toClear : toClear + ((percent - fromClear) / span) * (toSolid - toClear);
};

const remapStops = (
  stops: StageWashStops,
  fromClear: number,
  fromSolid: number,
  toClear: number,
  toSolid: number,
): StageWashStops =>
  stops.map(([percent, alpha]) => [
    remapPercent(percent, fromClear, fromSolid, toClear, toSolid),
    alpha,
  ]);

export const resolveIconDropWashPreset = (
  theme?: Pick<QuickstartTheme, 'thankYouWashPreset' | 'thankYouInstallIconStyle' | 'gameLogo'>,
): IconDropWashPresetName => {
  if (theme?.thankYouWashPreset === 'lower' || theme?.thankYouWashPreset === 'raised') {
    return theme.thankYouWashPreset;
  }
  return 'lower';
};

/**
 * Icon Drop interstitial wash. Empty / Auto, square, wordmark, raised, and
 * lower all share Anika's UI Shell stops (media 100, clear 26, solid 90).
 * Platform-agnostic: iOS Safari/Chrome and Android Chrome share these stops
 * — no UA or media-query fork.
 */
export const resolveIconDropStageWash = (
  theme?: Pick<
    QuickstartTheme,
    | 'thankYouWashPreset'
    | 'thankYouMediaBandPercent'
    | 'thankYouWashClearUntil'
    | 'thankYouWashSolidAt'
    | 'thankYouInstallIconStyle'
    | 'gameLogo'
  >,
): IconDropStageWash => {
  const preset = PRESETS[resolveIconDropWashPreset(theme)];
  const fromClear = lastClear(preset.washStops);
  const fromSolid = firstSolid(preset.washStops);
  const toClear = theme?.thankYouWashClearUntil ?? fromClear;
  const toSolid = theme?.thankYouWashSolidAt ?? fromSolid;
  const remap = toClear !== fromClear || toSolid !== fromSolid;

  return {
    mediaBand: `${theme?.thankYouMediaBandPercent ?? preset.mediaBandPercent}%`,
    washStops: remap ? remapStops(preset.washStops, fromClear, fromSolid, toClear, toSolid) : preset.washStops,
    maskStops: remap ? remapStops(preset.maskStops, fromClear, fromSolid, toClear, toSolid) : preset.maskStops,
  };
};
